import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { cardAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  CreditCard, 
  Check,
  ExternalLink,
  X,
  Unlink,
  AlertTriangle,
  Palette
} from 'lucide-react';

// Card design catalogue
const CARD_DESIGNS = [
  { key: 'midnight', name: 'Midnight', dot: '#334155', from: '#0f172a', to: '#1e293b' },
  { key: 'violet',   name: 'Violet',   dot: '#7c3aed', from: '#3b0764', to: '#6d28d9' },
  { key: 'ocean',    name: 'Ocean',    dot: '#0284c7', from: '#0c4a6e', to: '#0369a1' },
  { key: 'ember',    name: 'Ember',    dot: '#ea580c', from: '#7c2d12', to: '#c2410c' },
  { key: 'forest',   name: 'Forest',   dot: '#059669', from: '#064e3b', to: '#065f46' },
  { key: 'rose',     name: 'Rose',     dot: '#e11d48', from: '#881337', to: '#9f1239' },
];

const getDesign = (key) => CARD_DESIGNS.find(d => d.key === key) || CARD_DESIGNS[0];

// Association Modal
const AssociationModal = ({ isOpen, card, onClose, onSubmit, loading }) => {
  const [url, setUrl] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    onSubmit(url);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay flex items-center justify-center p-4" data-testid="association-modal">
      <motion.div
        className="glass rounded-3xl p-8 w-full max-w-lg"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Link Your Card</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <p className="text-slate-400 mb-6">
          Enter the URL you want people to be redirected to when they tap or scan your MeTag card.
          A MeeLink page works well if that is already your public home.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Your Profile URL</label>
            <Input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              className="input-field"
              placeholder="https://your-profile-url.com"
              data-testid="card-url-input"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full btn-primary"
            disabled={loading}
            data-testid="link-card-submit-btn"
          >
            {loading ? 'Linking...' : 'Link Card'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default function CardsPage() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [unlinkConfirm, setUnlinkConfirm] = useState(null);
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [designCard, setDesignCard] = useState(null); // card whose design picker is open
  const [designSaving, setDesignSaving] = useState(false);
  const { getQRCode, clearQRCode } = useAuth();
  const qrCode = getQRCode();

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await cardAPI.getAll();
      setCards(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch cards');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkCard = (card) => {
    setSelectedCard(card);
    setShowModal(true);
  };

  const handleSubmitUrl = async (url) => {
    setLinkLoading(true);
    try {
      const urlCode = selectedCard?.urlCode || qrCode;
      await cardAPI.updateUrl({ url_code: urlCode, url });
      toast.success('Card linked successfully!');
      clearQRCode();
      setShowModal(false);
      fetchCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link card');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleUnlinkCard = async (card) => {
    setUnlinkLoading(true);
    try {
      await cardAPI.deleteCard(card.urlCode);
      toast.success('Card unlinked successfully. It can now be claimed again.');
      setUnlinkConfirm(null);
      fetchCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink card');
    } finally {
      setUnlinkLoading(false);
    }
  };

  const handleDesignChange = async (card, designKey) => {
    setDesignSaving(true);
    try {
      await cardAPI.updateDesign(card.urlCode, designKey);
      setCards(prev => prev.map(c => c.urlCode === card.urlCode ? { ...c, design: designKey } : c));
      toast.success(`Design changed to ${CARD_DESIGNS.find(d => d.key === designKey)?.name}!`);
      setDesignCard(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update design');
    } finally {
      setDesignSaving(false);
    }
  };

  // If there's a QR code that doesn't match any existing card, show option to link
  const showQRCodeCard = qrCode && !cards.some(card => card.urlCode === qrCode);

  return (
    <div data-testid="cards-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">My Cards</h1>
        <p className="text-slate-400">Manage and link your MeTag NFC cards</p>
      </div>

      {/* QR Code detected but not linked */}
      {showQRCodeCard && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-6 mb-6 border border-emerald-500/30"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">New Card Detected</h3>
              <p className="text-slate-400 text-sm">Code: {qrCode.slice(0, 8)}...</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowModal(true)}
            className="btn-primary"
            data-testid="link-new-card-btn"
          >
            Link This Card
          </Button>
        </motion.div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : cards.length === 0 && !showQRCodeCard ? (
        <div className="glass rounded-2xl p-12 text-center">
          <CreditCard className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No cards yet</h3>
          <p className="text-slate-400 mb-4">
            Purchase a MeTag card and scan its QR code to link it to your profile.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {cards.map((card, index) => {
            const design = getDesign(card.design || 'midnight');
            const isDesignOpen = designCard?.urlCode === card.urlCode;

            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="glass rounded-2xl overflow-hidden"
              >
                {/* Card visual banner */}
                <div
                  className="h-28 flex items-center px-6 gap-4"
                  style={{ background: `linear-gradient(135deg, ${design.from}, ${design.to})` }}
                >
                  <CreditCard className="w-10 h-10 text-white/60" />
                  <div className="flex-1">
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-0.5">MeTag Card</p>
                    <p className="text-white font-mono text-sm">
                      **** **** **** {card.urlCode?.slice(-4).toUpperCase()}
                    </p>
                  </div>
                  {card.isLinked && (
                    <span className="text-xs text-white/60 bg-white/10 px-2 py-0.5 rounded-full">Active</span>
                  )}
                </div>

                {/* Card info */}
                <div className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <p className="text-slate-500 text-xs mb-0.5">Card Code</p>
                      <p className="text-white font-mono text-sm">{card.urlCode?.slice(0, 8)}…</p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Design picker toggle */}
                      <Button
                        onClick={() => setDesignCard(isDesignOpen ? null : card)}
                        variant="outline"
                        className="text-xs btn-secondary flex items-center gap-1.5"
                        data-testid={`design-btn-${card.id}`}
                      >
                        <div className="w-3 h-3 rounded-full" style={{ background: design.dot }} />
                        <Palette className="w-3.5 h-3.5" />
                        Style
                      </Button>

                      {card.isLinked ? (
                        <>
                          <span className="status-badge status-completed flex items-center gap-1">
                            <Check className="w-4 h-4" />
                            Linked
                          </span>
                          <Button
                            onClick={() => handleLinkCard(card)}
                            variant="outline"
                            className="btn-secondary text-sm"
                            data-testid={`update-card-${card.id}`}
                          >
                            Update URL
                          </Button>
                          <Button
                            onClick={() => setUnlinkConfirm(card)}
                            variant="outline"
                            className="text-sm border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                            data-testid={`unlink-card-${card.id}`}
                          >
                            <Unlink className="w-4 h-4 mr-1" />
                            Unlink
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="status-badge status-pending">Not Linked</span>
                          <Button
                            onClick={() => handleLinkCard(card)}
                            className="btn-primary text-sm"
                            data-testid={`link-card-${card.id}`}
                          >
                            Link Card
                          </Button>
                          <Button
                            onClick={() => setUnlinkConfirm(card)}
                            variant="outline"
                            className="text-sm border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                            data-testid={`delete-card-${card.id}`}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Design picker panel */}
                  {isDesignOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 pt-4 border-t border-white/5"
                    >
                      <p className="text-slate-400 text-xs mb-3">Choose card style</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {CARD_DESIGNS.map(d => (
                          <button
                            key={d.key}
                            onClick={() => handleDesignChange(card, d.key)}
                            disabled={designSaving}
                            title={d.name}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                              card.design === d.key || (!card.design && d.key === 'midnight')
                                ? 'border-white/30 text-white'
                                : 'border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                            }`}
                            style={{
                              background: card.design === d.key || (!card.design && d.key === 'midnight')
                                ? `linear-gradient(135deg, ${d.from}99, ${d.to}99)`
                                : undefined
                            }}
                            data-testid={`design-option-${d.key}`}
                          >
                            <div className="w-3.5 h-3.5 rounded-full" style={{ background: d.dot }} />
                            {d.name}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {card.linkedUrl && (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <p className="text-slate-500 text-sm mb-1">Linked URL</p>
                      <a
                        href={card.linkedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-violet-400 hover:text-violet-300 flex items-center gap-1 text-sm"
                      >
                        {card.linkedUrl}
                        <ExternalLink className="w-4 h-4 flex-shrink-0" />
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <AssociationModal
        isOpen={showModal}
        card={selectedCard}
        onClose={() => {
          setShowModal(false);
          setSelectedCard(null);
        }}
        onSubmit={handleSubmitUrl}
        loading={linkLoading}
      />

      {/* Unlink Confirmation Modal */}
      {unlinkConfirm && (
        <div className="modal-overlay flex items-center justify-center p-4" data-testid="unlink-confirm-modal">
          <motion.div
            className="glass rounded-3xl p-8 w-full max-w-md"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Unlink Card?</h2>
                <p className="text-slate-400 text-sm">Card #{unlinkConfirm.urlCode?.slice(0, 8)}...</p>
              </div>
            </div>
            <p className="text-slate-400 mb-8">
              This will remove the card from your account. It can be re-claimed by scanning its QR code again.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setUnlinkConfirm(null)}
                variant="outline"
                className="flex-1 btn-secondary"
                data-testid="unlink-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleUnlinkCard(unlinkConfirm)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={unlinkLoading}
                data-testid="unlink-confirm-btn"
              >
                {unlinkLoading ? 'Removing...' : 'Yes, Remove Card'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
